/**
 * DeleteForm Component
 *
 * Form for deleting existing bookings. Supports:
 * - Selecting which projects/customers to delete
 * - Confirmation step for bulk deletes (5+ bookings)
 * - Batch deletion with progress feedback
 *
 * Flow:
 * 1. Select projects/customers to delete
 * 2. Confirm if deleting 5+ bookings
 * 3. Execute deletion
 * 4. Report results
 */

import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";

// Components
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Spinner } from "@/components/ui/feedback/Spinner";
import { Icon } from "@/components/ui/display/Icon";
import { generateId } from "@/lib/utils";

// Hooks
import { useBookingMutations } from "@/hooks/booking/useBookingMutations";

// Types
import type { IBooking } from "@/services/types";
import { BookingType } from "@/services/types";
import type {
  DeleteFormProps,
  DeleteFormState,
  DeleteSelectionItem,
} from "./types";
import {
  getProjectsFromCards,
  getCustomersFromCards,
  getBookingsByProject,
  getBookingsByCustomer,
  calculateTotalHours,
  getAllBookingsFromCards,
} from "./types";

// Utils
import { formatDecimal } from "@/utils/number/formatters";

// Styles
import styles from "./DeleteForm.module.css";
import sharedStyles from "../shared/formUtilities.module.css";

// Threshold for requiring confirmation
const CONFIRM_THRESHOLD = 5;

export const DeleteForm: React.FC<DeleteFormProps> = ({
  selectedCards,
  bookingType,
  // onCancel - available for future use
  onDone,
  onError,
  className,
  "data-testid": testId,
}) => {
  // Hooks
  const { deleteBookings } = useBookingMutations();

  // Derived data
  const isHardBooking = bookingType === "hard";

  const projectsInSelection = useMemo(
    () => getProjectsFromCards(selectedCards),
    [selectedCards],
  );

  const customersInSelection = useMemo(
    () => getCustomersFromCards(selectedCards),
    [selectedCards],
  );

  const allBookings = useMemo(
    () => getAllBookingsFromCards(selectedCards),
    [selectedCards],
  );

  // Form state
  const [state, setState] = useState<DeleteFormState>({
    step: "select",
    selectedProjectIds: [],
    selectedCustomerIds: [],
  });

  // Build selection items for display
  const selectionItems: DeleteSelectionItem[] = useMemo(() => {
    if (isHardBooking) {
      return projectsInSelection.map((project) => {
        const bookings = getBookingsByProject(selectedCards, project.id);
        return {
          id: project.id,
          name: project.name,
          code: project.code,
          bookingCount: bookings.length,
          totalHours: calculateTotalHours(bookings),
          isSelected: state.selectedProjectIds.includes(project.id),
        };
      });
    } else {
      return customersInSelection.map((customer) => {
        const bookings = getBookingsByCustomer(selectedCards, customer.id);
        return {
          id: customer.id,
          name: customer.name,
          bookingCount: bookings.length,
          totalHours: calculateTotalHours(bookings),
          isSelected: state.selectedCustomerIds.includes(customer.id),
        };
      });
    }
  }, [
    isHardBooking,
    projectsInSelection,
    customersInSelection,
    selectedCards,
    state.selectedProjectIds,
    state.selectedCustomerIds,
  ]);

  // Calculate bookings to delete
  const bookingsToDelete: IBooking[] = useMemo(() => {
    if (isHardBooking) {
      return allBookings.filter(
        (b) =>
          (b.project && state.selectedProjectIds.includes(b.project.id)) ||
          (state.selectedProjectIds.includes(-1) &&
            b.b_type === BookingType.ABSENCE),
      );
    } else {
      return allBookings.filter(
        (b) => b.customer && state.selectedCustomerIds.includes(b.customer.id),
      );
    }
  }, [
    isHardBooking,
    allBookings,
    state.selectedProjectIds,
    state.selectedCustomerIds,
  ]);

  const deleteCount = bookingsToDelete.length;
  const hasSelection = deleteCount > 0;
  const needsConfirmation = deleteCount >= CONFIRM_THRESHOLD;

  // Toggle selection handlers
  const toggleProject = useCallback((projectId: number) => {
    setState((prev) => ({
      ...prev,
      selectedProjectIds: prev.selectedProjectIds.includes(projectId)
        ? prev.selectedProjectIds.filter((id) => id !== projectId)
        : [...prev.selectedProjectIds, projectId],
    }));
  }, []);

  const toggleCustomer = useCallback((customerId: number) => {
    setState((prev) => ({
      ...prev,
      selectedCustomerIds: prev.selectedCustomerIds.includes(customerId)
        ? prev.selectedCustomerIds.filter((id) => id !== customerId)
        : [...prev.selectedCustomerIds, customerId],
    }));
  }, []);

  const handleToggle = useCallback(
    (id: number) => {
      if (isHardBooking) {
        toggleProject(id);
      } else {
        toggleCustomer(id);
      }
    },
    [isHardBooking, toggleProject, toggleCustomer],
  );

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (state.step === "confirm") {
      setState((prev) => ({ ...prev, step: "select" }));
    }
  }, [state.step]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    // If needs confirmation and not yet confirmed, show confirmation
    if (needsConfirmation && state.step === "select") {
      setState((prev) => ({ ...prev, step: "confirm" }));
      return;
    }

    // Execute deletion
    setState((prev) => ({ ...prev, step: "deleting" }));

    const count = bookingsToDelete.length;
    const noun = count === 1 ? "booking" : "bookinger";

    try {
      await deleteBookings(bookingsToDelete);
      onDone?.(`Slettet ${count} ${noun}`);
    } catch (error) {
      const message =
        error instanceof Error && error.message === "FORBIDDEN"
          ? "Ikke autorisert til å lagre endringer"
          : error instanceof Error
            ? error.message
            : "Kunne ikke slette bookinger";
      onError?.(message);
      setState((prev) => ({ ...prev, step: "select" }));
    }
  }, [
    needsConfirmation,
    state.step,
    bookingsToDelete,
    deleteBookings,
    onDone,
    onError,
  ]);

  // Format stats for display
  const formatStats = (hours: number, count: number): string => {
    const hourText = formatDecimal(hours);
    const bookingText = count === 1 ? "booking" : "bookinger";
    return `${hourText}t, ${count} ${bookingText}`;
  };

  // Render loading state
  if (state.step === "deleting") {
    return (
      <div
        className={classNames(styles.container, className)}
        data-testid={testId}
      >
        <div
          className={sharedStyles.loadingContainer}
          data-testid="delete-form-deleting"
        >
          <Spinner size="lg" />
          <span className={sharedStyles.loadingText}>Sletter bookinger...</span>
        </div>
      </div>
    );
  }

  // Render confirmation step
  if (state.step === "confirm") {
    const noun = deleteCount === 1 ? "booking" : "bookinger";

    return (
      <div
        className={classNames(styles.container, className)}
        data-testid={testId}
      >
        <div className={styles.confirmStep} data-testid="delete-form-confirm">
          <div className={styles.warningBox}>
            <Icon name="alertTriangle" className={styles.warningIcon} />
            <span className={styles.warningText}>
              Er du sikker på at du vil slette {deleteCount} {noun}? Denne
              handlingen kan ikke angres.
            </span>
          </div>

          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>
                Bookinger som slettes:
              </span>
              <span className={styles.summaryValue}>{deleteCount}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Totalt antall timer:</span>
              <span className={styles.summaryValue}>
                {formatDecimal(calculateTotalHours(bookingsToDelete))}t
              </span>
            </div>
          </div>

          <div className={styles.actions}>
            <ActionButton
              label="Avbryt"
              icon="x"
              variant="secondary"
              onClick={handleCancel}
              data-testid="delete-form-cancel"
            />
            <ActionButton
              label="Slett"
              icon="trash2"
              variant="danger"
              onClick={handleDelete}
              data-testid="delete-form-confirm-delete"
            />
          </div>
        </div>
      </div>
    );
  }

  // Render selection step
  const entityType = isHardBooking ? "prosjekt" : "kunde";
  const entityPlural = isHardBooking ? "prosjekter" : "kunder";
  const hasMultiple = selectionItems.length > 1;

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      <div data-testid="delete-form-select">
        <p className={styles.hint}>
          {hasMultiple
            ? `Valgte dager inneholder flere ${entityPlural}. Velg ${entityPlural} som skal slettes.`
            : `Velg ${entityType} for å bekrefte sletting.`}
        </p>

        {selectionItems.length === 0 ? (
          <div className={styles.emptyState}>
            Ingen {entityPlural} funnet i utvalget.
          </div>
        ) : (
          selectionItems.map((item) => (
            <div
              key={item.id}
              className={classNames(styles.selectionRow, {
                [styles.disabled]: !item.isSelected,
              })}
              onClick={() => handleToggle(item.id)}
              data-testid={`delete-form-item-${item.id}`}
            >
              <div className={styles.nameSection}>
                <Icon
                  name={item.isSelected ? "trash2" : "trashOff"}
                  className={classNames(styles.deleteIcon, {
                    [styles.deleteIconDisabled]: !item.isSelected,
                  })}
                />
                <span
                  className={classNames(styles.name, {
                    [styles.nameDisabled]: !item.isSelected,
                  })}
                >
                  {item.code ? `${item.code} - ${item.name}` : item.name}
                </span>
              </div>
              <div className={styles.infoSection}>
                <span className={styles.stats}>
                  {formatStats(item.totalHours, item.bookingCount)}
                </span>
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    id={generateId("delete-form-checkbox", item.id)}
                    checked={item.isSelected}
                    onChange={() => handleToggle(item.id)}
                    data-testid={`delete-form-checkbox-${item.id}`}
                  />
                </div>
              </div>
            </div>
          ))
        )}

        <div className={styles.actions}>
          <ActionButton
            label={needsConfirmation ? "Slett valgte" : "Slett"}
            icon="trash2"
            variant="danger"
            onClick={handleDelete}
            disabled={!hasSelection}
            data-testid="delete-form-submit"
          />
        </div>
      </div>
    </div>
  );
};

DeleteForm.displayName = "DeleteForm";
